// What a repertoire's page is showing, and how each press changes it: the
// position on the line, a habit and where it leads, or moves tried off the
// line. No DOM and no requests — the page (Repertoire.tsx) draws a RepState
// and hands every press to a function here — so all of it is played in Node
// (tests/web/repertoire.test.mjs), the way the board's puzzle.ts is.
//
// The address is one of these functions too: addressOf says where the page
// is, fromAddress puts it back, and the page writes the first from a single
// place, so a reload, Back into the page or a link opens what was on it.

import { Chess } from "chess.js";
import { standardLine, type Score } from "../board/lines.ts";
import type { Quirk } from "../board/sly.ts";

export interface TreeMove {
  uci: string;
  san: string;
  to: string;
  main?: boolean;
  nag?: string;
  comment?: string;
  flag?: "unsound" | "dubious";
  trap?: boolean;
  added?: boolean;
  white_eval?: Score | null;
  /** For a reply prepared for: the share of games at your level that play
   *  it, as the server read it from the book. */
  share?: number;
}

export interface TreeNode {
  fen: string;
  ply: number;
  name?: string;
  moves?: TreeMove[];
}

export interface Habit {
  epd: string;
  fen: string;
  move_number: number;
  played_san: string;
  played_uci: string;
  played_fen?: string;
  prepared?: string[];
  games?: { game_id: string; color: string; played_at?: number }[];
  avg_lost: number;
  avg_before?: number | null;
  avg_after?: number | null;
}

/** A move tried off the line, and the position it reached. */
export interface Tried {
  uci: string;
  san: string;
  fen: string;
  /** What the board said about it where it was played, when it was one of
   *  the moves the book knows and the line does not: the board offered it,
   *  so what is said of it afterwards is the same thing. */
  quirk?: Quirk;
}

/**
 * What the board is showing: the line; a habit, and where it leads; or moves
 * tried off the line from the position selected on it — either side's, as
 * many as you like — to see what the book and the engine make of them.
 */
export type View = { kind: "line" } | { kind: "habit"; hb: Habit } | { kind: "off"; hb: Habit } | { kind: "trial"; steps: Tried[]; at: number };

/** Where on the line the page is: a position, and the move that reached it. */
export interface At {
  nodeEpd: string;
  mv: TreeMove | null;
}

export interface RepState {
  at: At;
  view: View;
}

// --- SMALL THINGS ---

export const bare = (san: string) => String(san || "").replace(/[+#!?]/g, "");
export const sideToMove = (fen: string) => (String(fen).split(/\s+/)[1] === "b" ? "black" : "white");
export const byMain = (moves: TreeMove[] = []) => moves.slice().sort((a, b) => Number(Boolean(b.main)) - Number(Boolean(a.main)));
export const keyOf = (nodeEpd: string, mv: TreeMove) => `${nodeEpd}|${mv.uci}`;
export const samePosition = (a: string, b: string) => a.split(" ").slice(0, 4).join(" ") === b.split(" ").slice(0, 4).join(" ");

/** A position's key as the server writes it: the FEN's first four fields,
 *  the en passant square only when a capture there is legal. */
export function epdOf(fen: string): string {
  const fields = fen.split(/\s+/);
  let ep = fields[3] || "-";
  try {
    if (ep !== "-" && !new Chess(fen).moves({ verbose: true }).some((m) => m.flags.includes("e"))) ep = "-";
  } catch {
    ep = "-";
  }
  return [fields[0], fields[1], fields[2], ep].join(" ");
}

// --- THE TREE, READ ONCE ---
//
// A move list, the way a study writes one: the main line runs on, and each
// branch point folds its other moves behind a "+N". The outline is walked
// once per answer from the server: for the tree when it is shown, and for
// which move reaches each position, which is how every other part of the
// page finds its way along the line.

export type Item =
  | { t: "move"; nodeEpd: string; mv: TreeMove; node: TreeNode; number: boolean }
  | { t: "name"; name: string }
  | { t: "start" }
  | { t: "toggle"; at: string; count: number; yoursNext: boolean }
  | { t: "plus"; epd: string }
  | { t: "transposes" };

export type Block = { t: "row"; items: Item[] } | { t: "fold"; at: string; variations: Block[][] };

export interface Outline {
  blocks: Block[];
  /** The visible move into each position. */
  reachedBy: Map<string, { nodeEpd: string; mv: TreeMove }>;
  /** The folds a move sits inside, outermost first. */
  foldsOf: Map<string, string[]>;
  branchPoints: number;
}

export function outline(root: string | undefined, nodes: Record<string, TreeNode>, color: string): Outline {
  const reachedBy = new Map<string, { nodeEpd: string; mv: TreeMove }>();
  const foldsOf = new Map<string, string[]>();
  const stack: string[] = [];
  const seen = new Set<string>();
  let branchPoints = 0;
  const move = (node: TreeNode, nodeEpd: string, mv: TreeMove, number: boolean): Item => {
    foldsOf.set(keyOf(nodeEpd, mv), stack.slice());
    if (!reachedBy.has(mv.to)) reachedBy.set(mv.to, { nodeEpd, mv });
    return { t: "move", nodeEpd, mv, node, number: number || node.ply % 2 === 0 };
  };
  function line(start: string, forceNumber: boolean, lastName: string | null): Block[] {
    const blocks: Block[] = [];
    let row: Item[] = [];
    let at: string | undefined = start;
    let force = forceNumber;
    let name = lastName;
    while (at) {
      const node: TreeNode | undefined = nodes[at];
      if (!node || !(node.moves || []).length) {
        // Where a line ends is where it can be carried on from.
        if (node) row.push({ t: "plus", epd: at });
        break;
      }
      if (seen.has(at)) {
        row.push({ t: "transposes" });
        break;
      }
      seen.add(at);
      if (node.name && node.name !== name) {
        row.push({ t: "name", name: node.name });
        name = node.name;
        force = true;
      }
      const [main, ...alts] = byMain(node.moves);
      if (alts.length) {
        // This position has several moves: the row ends with the toggle that
        // opens the others, and the main move starts the next row.
        branchPoints += 1;
        stack.push(at);
        const variations = alts.map((alt) => {
          const rows = line(alt.to, false, name);
          const first = rows[0];
          const head = move(node, at!, alt, true);
          if (first && first.t === "row") first.items.unshift(head);
          else rows.unshift({ t: "row", items: [head] });
          return rows;
        });
        stack.pop();
        if (at === root && !row.length) row.push({ t: "start" });
        row.push({ t: "toggle", at, count: alts.length, yoursNext: sideToMove(node.fen) === color });
        blocks.push({ t: "row", items: row }, { t: "fold", at, variations });
        row = [];
        force = true;
      }
      row.push(move(node, at, main!, force));
      force = false;
      at = main!.to;
    }
    if (row.length) blocks.push({ t: "row", items: row });
    return blocks;
  }
  const blocks = root ? line(root, true, null) : [];
  return { blocks, reachedBy, foldsOf, branchPoints };
}

/** How far back "lately" reaches: where your games have left the line in the
 *  last month is what to drill now. */
export const LATELY_DAYS = 30;

/** Where your games left the line lately: at each position of yours, the
 *  games that played something else there, and beneath each position, all of
 *  those further down. A game leaves the line once, at its first move off it,
 *  so no game is counted twice along one path. */
export function missedLately(
  nodes: Record<string, TreeNode>,
  habits: Habit[],
  now = Date.now() / 1000
): { here: (epd: string) => number; beneath: (epd: string) => number; played: (epd: string) => { san: string; games: number }[] } {
  const since = now - LATELY_DAYS * 86400;
  const at = new Map<string, Set<string>>();
  const played = new Map<string, { san: string; games: number }[]>();
  for (const hb of habits) {
    const lately = (hb.games || []).filter((game) => (game.played_at ?? 0) >= since);
    if (!lately.length) continue;
    const games = at.get(hb.epd) ?? new Set<string>();
    for (const game of lately) games.add(game.game_id);
    at.set(hb.epd, games);
    played.set(hb.epd, [...(played.get(hb.epd) ?? []), { san: hb.played_san, games: lately.length }]);
  }
  const here = (epd: string) => at.get(epd)?.size ?? 0;
  const memo = new Map<string, number>();
  const beneath = (epd: string, path = new Set<string>()): number => {
    if (memo.has(epd)) return memo.get(epd)!;
    if (path.has(epd)) return 0;
    path.add(epd);
    let n = here(epd);
    for (const mv of nodes[epd]?.moves || []) n += beneath(mv.to, path);
    path.delete(epd);
    memo.set(epd, n);
    return n;
  };
  return { here, beneath: (epd) => beneath(epd), played: (epd) => played.get(epd) ?? [] };
}

// --- A POSITION'S MOVES, AS FEW AS WILL DO ---

/** Their replies shown before the rest are folded away: those at least this
 *  share of your level plays — "rare" is what fewer do — at most four, and
 *  the three most played where the book has too few games to say. */
export const COMMON_SHARE = 0.05;
export const SHOWN_REPLIES = 4;
const AT_LEAST = 3;
/** And how many of those that lose for them. */
export const SHOWN_MISTAKES = 2;

/**
 * The moves from a position, grouped for a person learning them. Your own
 * moves are all shown: they are what the line is. Their replies are shown
 * the way a teacher would — the few most played at your level, then where
 * they most often go wrong, and the rest folded under "Rare": thirteen
 * replies at once is a reference book, not a lesson.
 */
export function groupMoves(moves: TreeMove[] = [], yours: boolean): { main: TreeMove[]; wrong: TreeMove[]; rare: TreeMove[] } {
  if (yours) return { main: byMain(moves), wrong: [], rare: [] };
  const byShare = (list: TreeMove[]) => list.slice().sort((a, b) => (b.share ?? -1) - (a.share ?? -1) || Number(Boolean(b.main)) - Number(Boolean(a.main)));
  const wrong = byShare(moves.filter((m) => m.trap)).slice(0, SHOWN_MISTAKES);
  const rest = byShare(moves.filter((m) => !wrong.includes(m)));
  const known = rest.some((m) => m.share !== undefined);
  const common = known ? rest.filter((m) => (m.share ?? 0) >= COMMON_SHARE).length : AT_LEAST;
  const shown = Math.min(SHOWN_REPLIES, Math.max(common, Math.min(AT_LEAST, rest.length)));
  return { main: rest.slice(0, shown), wrong, rare: rest.slice(shown) };
}

// --- WHAT THE PAGE SHOWS, AND WHAT A PRESS DOES TO IT ---

/** A repertoire as the page reads it: its positions, where it starts, and
 *  its outline. */
export interface Lines {
  root: string;
  nodes: Record<string, TreeNode>;
  outline: Outline;
}

export const start = (t: Lines): RepState => ({ at: { nodeEpd: t.root, mv: null }, view: { kind: "line" } });

/** The position on the line: where the move selected goes, or the node. */
export const lineEpd = (s: RepState) => (s.at.mv ? s.at.mv.to : s.at.nodeEpd);

/** A move of the line selected, or the start. */
export const select = (nodeEpd: string, mv: TreeMove | null): RepState => ({ at: { nodeEpd, mv }, view: { kind: "line" } });

/** A position of the line, by the move the outline reaches it with; the start
 *  when nothing does. */
export function reveal(t: Lines, epd: string): RepState {
  const into = t.outline.reachedBy.get(epd);
  return into ? select(into.nodeEpd, into.mv) : select(t.root, null);
}

/** The folds of the tree to open so that a position of it can be seen. */
export function foldsTo(t: Lines, epd: string): string[] {
  const into = t.outline.reachedBy.get(epd);
  return into ? [...(t.outline.foldsOf.get(keyOf(into.nodeEpd, into.mv)) || []), epd] : [];
}

/** The line's position under whatever is shown: what moves are tried from. */
export function lineFen(t: Lines, s: RepState): string {
  const node = t.nodes[s.at.nodeEpd];
  return (s.at.mv ? t.nodes[s.at.mv.to]?.fen : node?.fen) || node?.fen || "";
}

/** The position on the board, and the move to mark as having reached it. */
export function shown(t: Lines, s: RepState): { fen: string; last: string | { good?: string; bad?: string } | null } {
  const v = s.view;
  if (v.kind === "habit") {
    const hn = t.nodes[v.hb.epd];
    const main = (hn?.moves || []).find((m) => m.main) || hn?.moves?.[0];
    return { fen: hn?.fen || lineFen(t, s), last: { good: main?.uci, bad: v.hb.played_uci } };
  }
  if (v.kind === "off" && v.hb.played_fen) return { fen: v.hb.played_fen, last: v.hb.played_uci };
  if (v.kind === "trial" && v.at > 0) {
    const step = v.steps[v.at - 1]!;
    return { fen: step.fen, last: step.uci };
  }
  return { fen: lineFen(t, s), last: s.at.mv ? s.at.mv.uci : null };
}

/** Whether ← has anywhere to go: everywhere but the start of the line. */
export const canStepBack = (t: Lines, s: RepState) => s.view.kind !== "line" || t.outline.reachedBy.has(lineEpd(s));

/** ←: one move back along what is shown — the line, what was tried, a habit
 *  back to the line, a habit's move back to the habit. */
export function stepBack(t: Lines, s: RepState): RepState {
  const v = s.view;
  if (v.kind === "habit") return reveal(t, v.hb.epd);
  if (v.kind === "off") return { ...s, view: { kind: "habit", hb: v.hb } };
  if (v.kind === "trial") return v.at > 0 ? { ...s, view: { ...v, at: v.at - 1 } } : select(s.at.nodeEpd, s.at.mv);
  const into = t.outline.reachedBy.get(lineEpd(s));
  return into ? reveal(t, into.nodeEpd) : s;
}

/** →, where it is a step: on along the line's main move, or on along what was
 *  tried and stepped back through. Null where → asks for something new — the
 *  book's move or the engine's past the last move tried — or has nothing. */
export function stepOn(t: Lines, s: RepState): RepState | null {
  const v = s.view;
  if (v.kind === "trial") return v.at < v.steps.length ? { ...s, view: { ...v, at: v.at + 1 } } : null;
  if (v.kind !== "line") return null;
  const onward = byMain(t.nodes[lineEpd(s)]?.moves)[0];
  return onward ? reveal(t, onward.to) : null;
}

/** A move made on the board. On the line, one of the line's own moves walks
 *  the line; anything else is tried, from wherever the board is — and
 *  stepped back along what was tried, the same move again goes on along it
 *  rather than starting it over. Null for a move that is not legal. */
export function play(t: Lines, s: RepState, uci: string, quirk?: Quirk): RepState | null {
  if (s.view.kind === "line") {
    const from = lineFen(t, s);
    const own = (t.nodes[lineEpd(s)]?.moves || []).find((m) => sameMove(from, m.uci, uci));
    if (own) return reveal(t, own.to);
  }
  if (s.view.kind !== "line" && s.view.kind !== "trial") return null;
  const trial = s.view.kind === "trial" ? s.view : { kind: "trial" as const, steps: [], at: 0 };
  if (trial.steps[trial.at]?.uci === uci) return { ...s, view: { ...trial, at: trial.at + 1 } };
  const fen = shown(t, s).fen;
  const game = new Chess(fen);
  let san: string;
  try {
    const move = game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.slice(4, 5) || "q" });
    if (!move) return null;
    san = move.san;
  } catch {
    return null;
  }
  return { ...s, view: { kind: "trial", steps: [...trial.steps.slice(0, trial.at), { uci, san, fen: game.fen(), quirk }], at: trial.at + 1 } };
}

/** Castling written as the king's two steps or as taking the rook is one
 *  move: the tree and the board do not always agree which. */
function sameMove(fen: string, a: string, b: string): boolean {
  return a === b || standardLine(fen, [a])[0] === standardLine(fen, [b])[0];
}

/** A habit on the board: the position, the line's move and yours. */
export const showHabit = (t: Lines, hb: Habit): RepState => ({ ...reveal(t, hb.epd), view: { kind: "habit", hb } });
/** Where a habit's move goes, as the book sees it. */
export const showHabitMove = (s: RepState, hb: Habit): RepState => ({ ...s, view: { kind: "off", hb } });
/** One of the moves tried, chosen from their list. */
export const toTried = (s: RepState, i: number): RepState => (s.view.kind === "trial" ? { ...s, view: { ...s.view, at: Math.max(0, Math.min(i, s.view.steps.length)) } } : s);

// --- THE ADDRESS ---

export interface Address {
  /** The line's moves from the start, in SAN without marks, joined by "_". */
  line: string | null;
  /** Moves tried from there, in UCI, joined by ",". */
  tried: string | null;
  /** How far along what was tried, when stepped back from its end. */
  at: number | null;
  /** A habit shown: the move your games play at the line's position. */
  habit: string | null;
  /** And where that move goes, shown. */
  off: 1 | null;
}

/** The moves from the start of the tree to a position of it; none when the
 *  outline does not reach it. */
export function movesTo(t: Lines, epd: string): TreeMove[] {
  const out: TreeMove[] = [];
  for (let p = epd, steps = 0; p !== t.root && steps < 300; steps += 1) {
    const into = t.outline.reachedBy.get(p);
    if (!into) return [];
    out.unshift(into.mv);
    p = into.nodeEpd;
  }
  return out;
}

/** The line's moves to a position, in SAN without marks. */
export const lineTo = (t: Lines, epd: string): string[] => movesTo(t, epd).map((m) => bare(m.san));

/** Where the page is, as the address says it: the line's position, and a
 *  habit shown there or the moves tried from there. */
export function addressOf(t: Lines, s: RepState): Address {
  const v = s.view;
  const none = { tried: null, at: null, habit: null, off: null } as const;
  if (v.kind === "habit" || v.kind === "off") {
    return { ...none, line: lineTo(t, v.hb.epd).join("_") || null, habit: v.hb.played_uci, off: v.kind === "off" ? 1 : null };
  }
  const line = lineTo(t, lineEpd(s)).join("_") || null;
  if (v.kind !== "trial" || !v.steps.length) return { ...none, line };
  return { ...none, line, tried: v.steps.map((step) => step.uci).join(","), at: v.at < v.steps.length ? v.at : null };
}

/** The page put back where an address says: as far along the line as its
 *  moves go, then as many of the moves tried as are legal from there. */
export function fromAddress(t: Lines, a: Partial<Address>, habits: Habit[] = []): RepState {
  let p = t.root;
  for (const san of (a.line || "").split("_").filter(Boolean).map((s) => bare(s))) {
    const mv = (t.nodes[p]?.moves || []).find((m) => bare(m.san) === san);
    if (!mv || !t.nodes[mv.to]) break;
    p = mv.to;
  }
  const s = p === t.root ? start(t) : reveal(t, p);
  if (a.habit) {
    const hb = habits.find((h) => h.epd === p && h.played_uci === a.habit);
    if (!hb) return s;
    const habit = showHabit(t, hb);
    return a.off ? showHabitMove(habit, hb) : habit;
  }
  const from = t.nodes[lineEpd(s)];
  if (!a.tried || !from) return s;
  const game = new Chess(from.fen);
  const steps: Tried[] = [];
  for (const uci of a.tried.split(",")) {
    try {
      const move = game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.slice(4, 5) || undefined });
      if (!move) break;
      steps.push({ uci, san: move.san, fen: game.fen() });
    } catch {
      break;
    }
  }
  if (!steps.length) return s;
  const at = a.at === null || a.at === undefined || Number.isNaN(a.at) ? steps.length : Math.max(0, Math.min(a.at, steps.length));
  return { ...s, view: { kind: "trial", steps, at } };
}
